// Price list DAO — data access for price_lists.
// One method per DB concern; no business logic.

import { BaseDao, type SupabaseClient } from '../base.ts';
import type { PostgrestError } from '@supabase/supabase-js';
import type { PriceList } from '../../types/price_list.ts';

export class PriceListDao extends BaseDao<PriceList> {
  protected readonly dependentResources = ['price_lists', 'transactions'];

  constructor(client: SupabaseClient) {
    super(client, 'price_lists');
  }

  /** Return the total number of non-deleted transactions priced via this cennik.
   *  Matches both load_price_list_id and unload_price_list_id. */
  async countLinkedTransactions(priceListId: number): Promise<number> {
    const { count, error } = await this.sb
      .from('transactions')
      .select('id', { count: 'exact', head: true })
      .eq('is_deleted', false)
      .or(
        `load_price_list_id.eq.${priceListId},unload_price_list_id.eq.${priceListId}`,
      );

    if (error) throw error;
    return count ?? 0;
  }

  /** RPC: list transactions that match this price-list's criteria (used by the
   *  "apply to existing transactions" picker). */
  async findMatchingTransactions(args: {
    product_id: number;
    side: 'load' | 'unload';
    company_id: number;
    load_place_company_id?: number | null;
    unload_place_company_id?: number | null;
    incoterms?: string | null;
    valid_from?: string | null;
    valid_to?: string | null;
  }): Promise<unknown[]> {
    // The client is schema-untyped, so the RPC response is `any` — narrow it here.
    const { data, error } = (await this.sb.rpc('find_transactions_for_price_list', {
      _product_id: args.product_id,
      _side: args.side,
      _company_id: args.company_id,
      _load_place_company_id: args.load_place_company_id ?? null,
      _unload_place_company_id: args.unload_place_company_id ?? null,
      _incoterms: args.incoterms ?? null,
      _valid_from: args.valid_from ?? null,
      _valid_to: args.valid_to ?? null,
    })) as { data: unknown[] | null; error: PostgrestError | null };
    if (error) throw error;
    return data ?? [];
  }

  /** Find the most recent non-deleted price-list in the same scope
   *  (product/side/company/places) whose validity starts AFTER the source row.
   *  Used by "extend" to warn when a newer list already exists. */
  async findNewerInSameScope(pl: PriceList): Promise<{ id: number; valid_from: string | null; valid_to: string | null; name: string | null } | null> {
    let q = this.sb
      .from('price_lists')
      .select('id, valid_from, valid_to, name')
      .eq('product_id', pl.product_id)
      .eq('side', pl.side)
      .eq('company_id', pl.company_id)
      .eq('is_deleted', false)
      .gt('valid_from', pl.valid_from ?? '1900-01-01')
      .order('valid_from', { ascending: false })
      .limit(1);
    q = pl.load_place_company_id == null
      ? q.is('load_place_company_id', null)
      : q.eq('load_place_company_id', pl.load_place_company_id);
    q = pl.unload_place_company_id == null
      ? q.is('unload_place_company_id', null)
      : q.eq('unload_place_company_id', pl.unload_place_company_id);
    const { data, error } = await q;
    if (error) throw error;
    return (data?.[0] as { id: number; valid_from: string | null; valid_to: string | null; name: string | null } | undefined) ?? null;
  }

  /** Apply identical pricing fields to many transactions in one PATCH. */
  async applyToTransactions(transactionIds: number[], fields: Record<string, unknown>): Promise<void> {
    if (transactionIds.length === 0) return;
    const { error } = await this.sb
      .from('transactions')
      .update(fields)
      .in('id', transactionIds);
    if (error) throw error;
  }
}
